/**
 * Dismissible modal encouraging RAM Live customers with the RAM_LIVE_VISIBLE
 * feature flag to try the new RAM Portal. Shown once per day after login.
 *
 * Depends on platform API feature flags (via SsoUser), i18n, and the RAM
 * marketing redirect URL builder for portal hand-off.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { LocalizedText } from "../LocalizedText";
import { buildPortalMarketingUrl } from "../../utils/portalMarketingUrl";
import { NewCamerasPlatformModalProps } from "./interfaces";
import { useStyles } from "./styles";

/** TS 4.4 DOM lib types omit the native modal dialog API added in later lib versions. */
type ModalDialogElement = HTMLDialogElement & {
  showModal?: () => void;
  open?: boolean;
};

export const NewCamerasPlatformModalView: React.FC<NewCamerasPlatformModalProps> = ({
  open,
  salesforceId,
  salesforceContactId,
  onClose,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dialogRef = React.useRef<HTMLDialogElement>(null);

  React.useEffect(() => {
    const dialog = dialogRef.current as ModalDialogElement | null;
    if (!dialog || !open) {
      return;
    }

    const handleCancel = (event: Event) => {
      event.preventDefault();
      onClose();
    };

    dialog.addEventListener("cancel", handleCancel);

    if (typeof dialog.showModal === "function") {
      if (!dialog.open) {
        dialog.showModal();
      }
    } else {
      // jsdom and older browsers may lack showModal(); open attribute keeps tests visible.
      dialog.setAttribute("open", "");
    }

    return () => {
      dialog.removeEventListener("cancel", handleCancel);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const portalUrl = buildPortalMarketingUrl(salesforceId, salesforceContactId);

  return (
    <dialog
      ref={dialogRef}
      className={classes.dialog}
      aria-labelledby="new-cameras-platform-modal-title"
    >
      <div className={classes.modal}>
        <button
          type="button"
          className={classes.closeButton}
          onClick={onClose}
          aria-label={t("close")}
        >
          &times;
        </button>
        <div className={classes.content}>
          <div className={classes.text}>
            <div className={classes.subtitle}>
              <LocalizedText text="new_platform_modal.subtitle" />
            </div>
            <h1
              className={classes.heading}
              id="new-cameras-platform-modal-title"
            >
              <LocalizedText text="new_platform_modal.heading_prefix" />
              <br />
              <span className={classes.highlight}>
                <LocalizedText text="new_platform_modal.heading_highlight" />
              </span>
            </h1>
            <p className={classes.description}>
              <LocalizedText text="new_platform_modal.description" />
            </p>
            <a
              href={portalUrl}
              className={classes.cta}
              target="_blank"
              rel="noopener noreferrer"
            >
              <LocalizedText text="new_platform_modal.cta" />
            </a>
          </div>
          <div className={classes.imageContainer}>
            <img
              className={classes.image}
              src={`${process.env.PUBLIC_URL}/img/portal-ad.png`}
              alt={t("new_platform_modal.image_alt")}
            />
          </div>
        </div>
      </div>
    </dialog>
  );
};
